import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchMarket,
  requestPrediction,
  getCachedPrediction,
  API,
} from "../lib/api";
import Header from "../components/Header";
import AIForecastPanel from "../components/AIForecastPanel";
import ProGate from "../components/ProGate";
import StarButton from "../components/StarButton";
import { ArrowLeft, Calendar, ChartLineUp, Drop, Sparkle, ArrowSquareOut, TrendUp, TrendDown } from "@phosphor-icons/react";
import { formatUSD, formatPct, formatEndDate, formatSignedPct } from "../lib/format";
import { useAuth } from "../auth/AuthProvider";

function ProbBar({ yes }) {
  const y = Math.max(0, Math.min(1, yes ?? 0));
  return (
    <div className="flex h-2 w-full overflow-hidden bg-white/5">
      <div className="bg-yes" style={{ width: `${y * 100}%` }} />
      <div className="bg-no" style={{ width: `${(1 - y) * 100}%` }} />
    </div>
  );
}

export default function MarketDetail() {
  const { id } = useParams();
  const { isPro } = useAuth();
  const [market, setMarket] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [streamText, setStreamText] = useState("");
  const esRef = useRef(null);

  useEffect(() => {
    fetchMarket(id).then(setMarket).catch(() => setMarket(null));
    getCachedPrediction(id).then((p) => p && setForecast(p));
    return () => {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [id]);

  const runForecast = async (force = false) => {
    setLoadingForecast(true);
    setError(null);
    setStreamText("");
    // Use SSE stream for live token-by-token reasoning
    if (esRef.current) esRef.current.close();
    const es = new EventSource(`${API}/predict/${id}/stream`);
    esRef.current = es;
    let buffer = "";
    es.addEventListener("delta", (e) => {
      try {
        const { text } = JSON.parse(e.data);
        buffer += text;
        setStreamText(buffer);
      } catch {}
    });
    es.addEventListener("complete", (e) => {
      try {
        const fc = JSON.parse(e.data);
        setForecast(fc);
      } catch {}
      es.close();
      esRef.current = null;
      setLoadingForecast(false);
      setStreamText("");
    });
    es.addEventListener("error", (e) => {
      try {
        const { error: err } = JSON.parse(e.data || "{}");
        if (err) setError(err);
      } catch {}
    });
    es.addEventListener("end", () => {
      es.close();
      esRef.current = null;
      setLoadingForecast(false);
    });
    es.onerror = () => {
      // Network/parse error from EventSource itself
      es.close();
      esRef.current = null;
      setLoadingForecast(false);
      if (!buffer) setError("Stream connection failed");
    };
  };

  return (
    <div className="min-h-screen bg-background" data-testid="market-detail-page">
      <Header search={search} setSearch={setSearch} />

      <div className="mx-auto max-w-[1200px] px-6 py-8">
        <Link
          to="/"
          data-testid="back-link"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft size={14} /> Back to terminal
        </Link>

        {!market ? (
          <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest">
            Loading market…
          </div>
        ) : (
          <>
            <div className="border border-white/10 p-6 mb-6 bg-zinc-950/40">
              <div className="flex items-start gap-5">
                {market.image && (
                  <img
                    src={market.image}
                    alt=""
                    className="w-16 h-16 object-cover border border-white/10"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 mb-2">
                    {market.category || "Polymarket"} · ID {market.id}
                  </div>
                  <h1 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
                    {market.question}
                  </h1>
                </div>
                <div className="shrink-0">
                  <StarButton market={market} size={22} />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <Stat label="YES" value={formatPct(market.yesPrice, 0)} accent="text-yes" />
                <Stat label="NO" value={formatPct(market.noPrice, 0)} accent="text-no" />
                <Stat
                  label="Volume"
                  value={formatUSD(market.volume)}
                  icon={ChartLineUp}
                  accent="text-profit"
                />
                <Stat
                  label="Liquidity"
                  value={formatUSD(market.liquidity)}
                  icon={Drop}
                  accent="text-yellow-400"
                />
              </div>

              <div className="mt-5">
                <ProbBar yes={market.yesPrice} />
              </div>

              <div className="mt-5 flex flex-wrap gap-4 text-xs font-mono text-zinc-400">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={12} /> {formatEndDate(market.endDate)} ·{" "}
                  {market.endDate && new Date(market.endDate).toLocaleDateString()}
                </span>
                {market.oneDayPriceChange != null && (
                  <span
                    className={`tabular-nums ${
                      Number(market.oneDayPriceChange) >= 0 ? "text-profit" : "text-loss"
                    }`}
                  >
                    24h {formatSignedPct(Number(market.oneDayPriceChange), 1)}
                  </span>
                )}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                {isPro ? (
                  <button
                    data-testid="run-forecast-btn"
                    onClick={() => runForecast(false)}
                    disabled={loadingForecast}
                    className="inline-flex items-center gap-2 bg-edge text-black font-display text-sm font-semibold uppercase tracking-[0.18em] px-5 py-2.5 hover:bg-white transition-colors disabled:opacity-60"
                  >
                    <Sparkle size={14} weight="fill" />
                    {loadingForecast ? "Streaming…" : forecast ? "Re-run AI Forecast" : "Run AI Forecast"}
                  </button>
                ) : (
                  <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                    ◆ AI forecast locked — scroll down to unlock with MFSMAI Pro
                  </span>
                )}
                {market.slug && (
                  <BetButtons
                    market={market}
                    forecast={forecast}
                  />
                )}
              </div>
            </div>

            {isPro ? (
              <AIForecastPanel
                forecast={forecast}
                loading={loadingForecast}
                streamText={streamText}
                error={error}
                onRefresh={() => runForecast(true)}
              />
            ) : (
              <ProGate feature="AI forecasts on this market" />
            )}

            {market.description && (
              <div className="border border-white/10 p-6 mt-6 bg-zinc-950/40">
                <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 mb-3">
                  Resolution Criteria
                </div>
                <p className="text-sm font-body text-zinc-300 whitespace-pre-wrap leading-relaxed">
                  {market.description}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, icon: Icon }) {
  return (
    <div className="border border-white/10 p-3">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500">
        <span>{label}</span>
        {Icon && <Icon size={14} weight="duotone" className={accent} />}
      </div>
      <div className={`mt-2 font-mono text-xl tabular-nums ${accent || ""}`}>{value}</div>
    </div>
  );
}

function BetButtons({ market, forecast }) {
  const slug = market.slug || market.id;
  const url = `https://polymarket.com/market/${slug}`;
  // Direction hint: if AI verdict YES/NO highlight that side; else use whichever
  // side AI assigns higher probability vs market.
  let preferredSide = null;
  let edge = 0;
  if (forecast) {
    edge = Number(forecast.edge || 0);
    if (forecast.verdict === "YES") preferredSide = "YES";
    else if (forecast.verdict === "NO") preferredSide = "NO";
    else preferredSide = edge > 0 ? "YES" : edge < 0 ? "NO" : null;
  }
  const yesBg = preferredSide === "YES"
    ? "bg-yes border-yes text-white"
    : "bg-transparent border-white/15 text-zinc-300 hover:border-white/40";
  const noBg = preferredSide === "NO"
    ? "bg-no border-no text-white"
    : "bg-transparent border-white/15 text-zinc-300 hover:border-white/40";

  const edgeLabel = forecast
    ? ` · AI edge ${edge >= 0 ? "+" : ""}${(edge * 100).toFixed(1)}%`
    : "";

  return (
    <div className="flex items-center gap-2" data-testid="bet-buttons">
      <a
        href={`${url}?side=YES&utm_source=mfsmai_oracle&utm_medium=app`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="bet-yes-btn"
        className={`inline-flex items-center gap-2 border px-3.5 py-2.5 text-xs font-display font-semibold uppercase tracking-[0.18em] transition-colors ${yesBg}`}
        title={`Open Polymarket to bet YES${edgeLabel}`}
      >
        <TrendUp size={13} weight="bold" />
        Bet YES @ {Math.round((market.yesPrice || 0) * 100)}¢
        <ArrowSquareOut size={11} />
      </a>
      <a
        href={`${url}?side=NO&utm_source=mfsmai_oracle&utm_medium=app`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="bet-no-btn"
        className={`inline-flex items-center gap-2 border px-3.5 py-2.5 text-xs font-display font-semibold uppercase tracking-[0.18em] transition-colors ${noBg}`}
        title={`Open Polymarket to bet NO${edgeLabel}`}
      >
        <TrendDown size={13} weight="bold" />
        Bet NO @ {Math.round((market.noPrice || 0) * 100)}¢
        <ArrowSquareOut size={11} />
      </a>
      {forecast && preferredSide && (
        <span
          data-testid="bet-edge-hint"
          className="hidden md:inline-flex items-center text-[10px] font-mono uppercase tracking-[0.22em] text-edge ml-1"
        >
          ◢ AI picks {preferredSide}
        </span>
      )}
    </div>
  );
}
